import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Stack,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { alpha } from '@mui/material/styles';
import Iconify from '../../components/iconify';
import { getAvailableBrands, getBrandConfigByDomain } from '../../config/brandConfig';
import { useAuthContext } from '../../auth/useAuthContext';
import { useSnackbar } from '../../components/snackbar';
import { getBrandFromCookie, setBrandCookie } from '../../utils/brandCookieUtils';

interface BrandOption {
  domain: string;
  name: string;
  code: string;
  description: string;
  greeting?: string;
}

// Dynamically generate brand options from available brands
const generateBrandOptions = (): BrandOption[] => {
  const availableBrands = getAvailableBrands();

  const options = availableBrands.map((domain) => {
    const config = getBrandConfigByDomain(domain);
    if (!config) return null;

    return {
      domain,
      name: config.name,
      code: config.brandCode,
      description: `${config.greeting} - ${config.locale} operations`,
      greeting: config.greeting
    };
  }).filter(Boolean) as BrandOption[];

  // Add default option
  options.push({
    domain: 'default',
    name: 'Dream Resorts (Default)',
    code: 'DRS',
    description: 'Default fallback brand for development'
  });

  return options;
};

const BRAND_OPTIONS = generateBrandOptions();

export default function BrandSelectorPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [currentBrand, setCurrentBrand] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const brand = getBrandFromCookie();
    setCurrentBrand(brand);
    setSelectedBrand(brand);
  }, []);

  const handleBrandChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedBrand(event.target.value);
    setError(null);
  };

  const handleApplyBrand = async () => {
    if (!selectedBrand) return;

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Set cookie for immediate brand switching
      setBrandCookie(selectedBrand);

      // Step 2: If user is authenticated, log them out so they can sign in with new brand
      if (isAuthenticated) {
        enqueueSnackbar(`Brand changed to ${BRAND_OPTIONS.find(b => b.domain === selectedBrand)?.name}. Please sign in again.`, {
          variant: 'info'
        });

        // Log out the user - they'll need to sign in again with the new brand
        await logout();

        // Add 750ms delay to let user read the snackbar message
        await new Promise(resolve => setTimeout(resolve, 750));

        // Force full page reload to login to ensure new brand config is loaded
        window.location.href = '/user/login';
      } else {
        enqueueSnackbar(`Brand set to ${BRAND_OPTIONS.find(b => b.domain === selectedBrand)?.name}`, {
          variant: 'success'
        });

        // Add 750ms delay to let user read the snackbar message
        await new Promise(resolve => setTimeout(resolve, 750));

        // For unauthenticated users, just reload to apply the new brand
        window.location.href = '/';
      }

    } catch (logoutError) {
      console.error('Error during brand change:', logoutError);
      setError('Failed to change brand. Please try again.');
      enqueueSnackbar('Failed to change brand', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueWithCurrent = () => {
    navigate('/');
  };

  const getCurrentBrandName = () => {
    const current = BRAND_OPTIONS.find(brand => brand.domain === currentBrand);
    return current?.name || 'Unknown Brand';
  };

  return (
    <>
      <Helmet>
        <title>Select Development Brand</title>
      </Helmet>

      <Container maxWidth="md" sx={{
        py: 4,
        maxWidth: { xs: '100%', sm: '650px' }
      }}>
        {/* Header with icon */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1.5
            }}
          >
            <Iconify icon="eva:color-palette-fill" width={24} />
          </Box>

          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Select Development Brand
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which brand configuration to use for this development session
          </Typography>

          {/* Authentication Status - Only show warning for authenticated users */}
          {isAuthenticated && user && (
            <Alert
              severity="warning"
              sx={{
                fontSize: '0.875rem',
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Box>Logged in as: <strong>{user.email}</strong></Box>
                <Box sx={{ mt: 0.25 }}>Brand changes will log you out.</Box>
              </Box>
            </Alert>
          )}
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        {/* Brand Selection */}
        <Paper sx={{ p: 2.5, mb: 2.5 }}>
          <Typography variant="h6" sx={{ mb: 0.25, fontSize: '1.1rem', textAlign: 'left' }}>
            Available Brands
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'left' }}>
            Select a brand to configure the portal experience
          </Typography>

          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <RadioGroup
              value={selectedBrand || ''}
              onChange={handleBrandChange}
              sx={{ gap: 0.5 }}
            >
              {BRAND_OPTIONS.map((brand) => (
                <Paper
                  key={brand.domain}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    border: selectedBrand === brand.domain ? 2 : 1,
                    borderColor: selectedBrand === brand.domain ? 'primary.main' : 'divider',
                    bgcolor: selectedBrand === brand.domain
                      ? alpha('#1976d2', 0.04)
                      : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: alpha('#1976d2', 0.02)
                    }
                  }}
                  onClick={() => setSelectedBrand(brand.domain)}
                >
                  <FormControlLabel
                    value={brand.domain}
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ ml: 0.5, textAlign: 'left' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.125, lineHeight: 1.1, textAlign: 'left' }}>
                          {brand.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.375, fontSize: '0.875rem', lineHeight: 1.2, textAlign: 'left' }}>
                          {brand.description}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: 'grey.100',
                            px: 0.75,
                            py: 0.125,
                            borderRadius: 0.5,
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            display: 'inline-block',
                            textAlign: 'left'
                          }}
                        >
                          {brand.code}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      m: 0,
                      width: '100%',
                      alignItems: 'flex-start',
                      '& .MuiFormControlLabel-label': { width: '100%' }
                    }}
                  />
                </Paper>
              ))}
            </RadioGroup>
          </FormControl>
        </Paper>

        {/* Action Buttons */}
        <Stack spacing={1.25} sx={{ alignItems: 'stretch' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleApplyBrand}
            disabled={!selectedBrand || selectedBrand === currentBrand || isLoading}
            sx={{ py: 1.125, fontSize: '0.95rem' }}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
                        {isLoading
              ? (isAuthenticated ? 'Changing Brand & Logging Out...' : 'Changing Brand...')
              : !selectedBrand
                ? 'Select a Brand'
                : `Use ${BRAND_OPTIONS.find(b => b.domain === selectedBrand)?.name}`
            }
          </Button>

          {currentBrand && (
            <Button
              variant="text"
              onClick={handleContinueWithCurrent}
              disabled={isLoading}
              sx={{ textDecoration: 'underline', py: 0.5, fontSize: '0.875rem' }}
            >
              Continue with current brand ({getCurrentBrandName()})
            </Button>
          )}
        </Stack>

        {/* Development Notice */}
        <Box sx={{
          mt: 3,
          pt: 1.5,
          textAlign: 'center',
          borderTop: 1,
          borderColor: 'divider'
        }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ lineHeight: 1 }}
          >
            <Iconify
              icon="eva:alert-triangle-outline"
              width={14}
              sx={{ color: 'warning.main', flexShrink: 0 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.75rem',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Development Mode Only - Not available in production
            </Typography>
          </Stack>
        </Box>
      </Container>
    </>
  );
}